import { useState, useEffect } from 'react';

interface LocationPermissionProps {
  onLocationReady: (hasLocation: boolean) => void;
  onCancel?: () => void;
}

type PermissionState = 'checking' | 'prompt' | 'requesting' | 'granted' | 'denied' | 'unavailable' | 'timeout';

export default function LocationPermission({ onLocationReady, onCancel }: LocationPermissionProps) {
  const [permissionState, setPermissionState] = useState<PermissionState>('checking');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check permission status on mount
  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setPermissionState('unavailable');
      setErrorMessage('Your browser does not support geolocation');
      return;
    }

    // Check permission status if available
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (result.state === 'granted') {
          // Already have permission, try to get location
          requestLocation();
        } else if (result.state === 'denied') {
          setPermissionState('denied');
          setErrorMessage('Location permission was denied. Please enable it in your browser settings.');
        } else {
          // Need to prompt
          setPermissionState('prompt');
        }
      } catch {
        // permissions API not supported, just try to get location
        setPermissionState('prompt');
      }
    } else {
      setPermissionState('prompt');
    }
  };

  const requestLocation = () => {
    setPermissionState('requesting');
    setErrorMessage(null);

    navigator.geolocation.getCurrentPosition(
      () => {
        // Success - we got a location
        setPermissionState('granted');
        onLocationReady(true);
      },
      (error) => {
        // Handle specific error types
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setPermissionState('denied');
            setErrorMessage('Location permission was denied. Please enable it in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setPermissionState('unavailable');
            setErrorMessage('Location information is unavailable on this device.');
            break;
          case error.TIMEOUT:
            setPermissionState('timeout');
            setErrorMessage('Location request timed out. This is common on desktop computers without GPS.');
            break;
          default:
            setPermissionState('unavailable');
            setErrorMessage('Unable to get your location.');
        }
      },
      {
        // This call is a permission *probe* - we only need a yes/no on
        // whether the browser will return a position. Low accuracy is
        // fine for the probe (faster on desktop without GPS), but we
        // don't need cached data for it. #1818
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleContinueWithoutLocation = () => {
    onLocationReady(false);
  };

  // If already granted, don't show anything
  if (permissionState === 'granted') {
    return null;
  }

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-4 flex items-start gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
          permissionState === 'denied' || permissionState === 'unavailable' || permissionState === 'timeout'
            ? 'bg-amber-100'
            : 'bg-blue-100'
        }`}>
          <svg className={`h-5 w-5 ${
            permissionState === 'denied' || permissionState === 'unavailable' || permissionState === 'timeout'
              ? 'text-amber-600'
              : 'text-blue-600'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-slate-800">
            {permissionState === 'checking' && 'Checking Location...'}
            {permissionState === 'prompt' && 'Location Permission Needed'}
            {permissionState === 'requesting' && 'Getting Location...'}
            {permissionState === 'denied' && 'Location Access Denied'}
            {permissionState === 'unavailable' && 'Location Unavailable'}
            {permissionState === 'timeout' && 'Location Timeout'}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {permissionState === 'checking' && 'Please wait while we check location permissions...'}
            {permissionState === 'prompt' && 'We use your location to track mileage and verify store check-ins. This is optional.'}
            {permissionState === 'requesting' && 'Please allow location access when prompted by your browser...'}
            {errorMessage && (permissionState === 'denied' || permissionState === 'unavailable' || permissionState === 'timeout') && errorMessage}
          </p>
        </div>
      </div>

      {/* Loading spinner */}
      {(permissionState === 'checking' || permissionState === 'requesting') && (
        <div className="flex justify-center py-4">
          <svg className="h-8 w-8 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {/* Action buttons for prompt state */}
      {permissionState === 'prompt' && (
        <div className="flex gap-3">
          <button
            onClick={handleContinueWithoutLocation}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-3 font-medium text-slate-700 hover:bg-slate-50"
          >
            Skip Location
          </button>
          <button
            onClick={requestLocation}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            Enable Location
          </button>
        </div>
      )}

      {/* Action buttons for error states */}
      {(permissionState === 'denied' || permissionState === 'unavailable' || permissionState === 'timeout') && (
        <div className="space-y-3">
          {permissionState === 'timeout' && (
            <button
              onClick={requestLocation}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 font-medium text-blue-700 hover:bg-blue-100"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          )}

          {permissionState === 'denied' && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
              <p className="font-medium">To enable location:</p>
              <ol className="mt-1 ml-4 list-decimal text-slate-500">
                <li>Click the lock/info icon in your browser's address bar</li>
                <li>Find "Location" and set it to "Allow"</li>
                <li>Refresh the page</li>
              </ol>
            </div>
          )}

          <button
            onClick={handleContinueWithoutLocation}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700"
          >
            Continue Without Location
          </button>

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full rounded-lg px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
